import {FormControl} from "@angular/forms";
import {ParamMap, Params} from "@angular/router";
import {MetadataTypeId, EquipmentId, MetadataType, Equipment, Level, LevelId, Zone, ZoneNameId} from "@app/graphql-typings/graphql";
import {User, UserId} from "@app/layout/admin/services/api/user-interfaces";
import {PriorityId} from "@app/shared/api/model/type-ids";
import {RelativeDateParamNames, RelativeDateTimeRange} from "@app/shared/components/date-time-range-picker/date-time-range-picker.component";
import {SiteId, SiteInfo} from "@app/shared/services/api/access-sites.interface";
import {FormValues} from "@app/shared/util/angular/angular-utils";
import {AppConstants} from "@app/shared/util/app-constants";
import {TicketArchivedDisplayState} from "@app/layout/sites/tickets/ticket-utils";
import {I18nMessage} from "@app/shared/api/model/i18n";
import {Impact, Priority, Template, TemplateId} from "@app/layout/rules/template-editor/api/template";
import {TemplateCategoryId} from "@app/layout/rules/template-categories/models/template-category";
import {TemplateCategoryOption} from "@app/layout/rules/template-editor/api/types";
import {NgClass} from "@angular/common";
import {DurationOptions} from "@app/shared/components/duration-select/duration-select.component";
import {InjectionToken} from "@angular/core";
import {BaseTicketSearchParams, RuleState} from "@app/shared/services/api/tickets/tickets.interface";

export const ticketTypeParamsMapperToken = new InjectionToken<TicketSearchParamsMapper>('TicketTypeParamsMapperToken');

export interface TicketSearchParamsMapper<
  PageParams extends TicketSearchPageParams<any, any> = TicketSearchPageParams<any, any>,
  FormValues extends TicketSearchFormValues<any, any> = TicketSearchFormValues<any, any>,
  RequestParams extends BaseTicketSearchParams = BaseTicketSearchParams> {
  pageParamsEqual(pageParams1: PageParams | null, pageParams2: PageParams | null): boolean;
  queryParamsToPageParams(queryParamMap: ParamMap): PageParams;
  pageParamsToQueryParams(pageParams: Partial<PageParams>, queryParams?: Params): Params;
  formValuesToPageParams(formValue: Partial<FormValues>, original?: Partial<PageParams> | null): PageParams;
  pageParamsToFormValues(pageParams: Partial<PageParams>): FormValues;
  pageParamsToRequestParams(pageParams: PageParams | null, timezone: string): RequestParams;
}

export interface TicketSearchPageParams<StatusId, ImpactId> {
  orderBy: string[] | null;
  summary: string | null;
  siteIds: SiteId[] | null;
  assigneeIds: UserId[] | null;
  priorityIds: PriorityId[] | null;
  statusIds: StatusId[] | null;
  updatedAt: RelativeDateTimeRange | null;
  createdAt: RelativeDateTimeRange | null;
  resolvedAt: RelativeDateTimeRange | null;
  notResolved: boolean | null;
  impactIds: ImpactId[] | null;
  metadataTypeIds: MetadataTypeId[] | null;
  equipmentIds: EquipmentId[] | null;
  levelIds: LevelId[] | null;
  zoneNameIds: ZoneNameId[] | null;
  ruleIds: string | null;
  archiveState: TicketArchivedDisplayState | null;
  viewSavings: ViewSavingsState | null;
  savingsState: SavingsState | null;
  selectAssociatedSites: boolean | null;
  linked: boolean | null;
  linkedAtLocalEnd: moment.Moment | null;
}

export interface TicketSearchForm<StatusId, ImpactId> {
  siteIds: FormControl<SiteId[] | null>;
  summary: FormControl<string | null>;
  assigneeIds: FormControl<UserId[] | null>;
  priorityIds: FormControl<PriorityId[] | null>;
  statusIds: FormControl<StatusId[] | null>;
  updatedAt: FormControl<RelativeDateTimeRange | null>;
  createdAt: FormControl<RelativeDateTimeRange | null>;
  resolvedAt: FormControl<RelativeDateTimeRange | null>;
  notResolved: FormControl<boolean | null>;
  impactIds: FormControl<ImpactId[] | null>;
  metadataTypeIds: FormControl<MetadataTypeId[] | null>;
  equipmentIds: FormControl<EquipmentId[] | null>;
  levelIds: FormControl<LevelId[] | null>;
  zoneNameIds: FormControl<ZoneNameId[] | null>;
  archiveState: FormControl<TicketArchivedDisplayState | null>;
  viewSavings: FormControl<ViewSavingsState | null>;
  savingsState: FormControl<SavingsState | null>;
  templateIds: FormControl<TemplateId[] | null>;
  templateCategoryIds: FormControl<TemplateCategoryId[] | null>;
  ruleStates: FormControl<RuleState[] | null>;
  timeInFault: FormControl<DurationOptions[] | null>;
  ruleIds: FormControl<string | null>;
  commentAuthorIds: FormControl<UserId[] | null>;
  ticketAge: FormControl<DurationOptions[] | null>;
  inFaultAt: FormControl<RelativeDateTimeRange | null>;
  ageCutoff: FormControl<moment.Moment | null>;
  linked: FormControl<boolean | null>;
  linkedAtLocalEnd: FormControl<moment.Moment | null>;
}

export type TicketSearchFormValues<StatusId, ImpactId> = Partial<FormValues<TicketSearchForm<StatusId, ImpactId>>>;

export const TicketSearchQueryParamConstants = {
  UPDATED_DATE_PREFIX: 'updated_at',
  CREATED_DATE_PREFIX: 'created_at',
  RESOLVED_DATE_PREFIX: 'resolved_at',
  START_SUFFIX: '_start',
  END_SUFFIX: '_end',
  INCLUDE_TODAY_SUFFIX: '_include_today',
  RELATIVE_DATE_SUFFIX: '_relative_date',
  LOCAL_DATE_START_SUFFIX: '_local_start',
  LOCAL_DATE_END_SUFFIX: '_local_end'
};

export const TicketSearchQueryParams = {
  TICKETS_ORDER_BY: 'tickets_order_by',
  UPDATED_AT_SORT_DIR: 'updated_at_sort_dir',
  SITE_IDS: 'site_ids',
  ASSIGNEE_IDS: 'assignee_ids',
  SUMMARY: 'summary',
  PRIORITY_IDS: 'priority_ids',
  STATUS_IDS: 'status_ids',
  UPDATED_AT_LOCAL_START: TicketSearchQueryParamConstants.UPDATED_DATE_PREFIX + TicketSearchQueryParamConstants.LOCAL_DATE_START_SUFFIX,
  UPDATED_AT_LOCAL_END: TicketSearchQueryParamConstants.UPDATED_DATE_PREFIX + TicketSearchQueryParamConstants.LOCAL_DATE_END_SUFFIX,
  UPDATED_AT_RELATIVE_DATE: 'updated' + TicketSearchQueryParamConstants.RELATIVE_DATE_SUFFIX,
  UPDATED_AT_INCLUDE_TODAY: TicketSearchQueryParamConstants.UPDATED_DATE_PREFIX + TicketSearchQueryParamConstants.INCLUDE_TODAY_SUFFIX,
  CREATED_AT_LOCAL_START: TicketSearchQueryParamConstants.CREATED_DATE_PREFIX + TicketSearchQueryParamConstants.LOCAL_DATE_START_SUFFIX,
  CREATED_AT_LOCAL_END: TicketSearchQueryParamConstants.CREATED_DATE_PREFIX + TicketSearchQueryParamConstants.LOCAL_DATE_END_SUFFIX,
  CREATED_AT_RELATIVE_DATE: 'created' + TicketSearchQueryParamConstants.RELATIVE_DATE_SUFFIX,
  CREATED_AT_INCLUDE_TODAY: TicketSearchQueryParamConstants.CREATED_DATE_PREFIX + TicketSearchQueryParamConstants.INCLUDE_TODAY_SUFFIX,
  RESOLVED_AT_LOCAL_START: TicketSearchQueryParamConstants.RESOLVED_DATE_PREFIX + TicketSearchQueryParamConstants.LOCAL_DATE_START_SUFFIX,
  RESOLVED_AT_LOCAL_END: TicketSearchQueryParamConstants.RESOLVED_DATE_PREFIX + TicketSearchQueryParamConstants.LOCAL_DATE_END_SUFFIX,
  RESOLVED_AT_RELATIVE_DATE: 'resolved' + TicketSearchQueryParamConstants.RELATIVE_DATE_SUFFIX,
  RESOLVED_AT_INCLUDE_TODAY: TicketSearchQueryParamConstants.RESOLVED_DATE_PREFIX + TicketSearchQueryParamConstants.INCLUDE_TODAY_SUFFIX,
  NOT_RESOLVED: 'not_resolved',
  IMPACT_IDS: 'impact_ids',
  METADATA_TYPE_IDS: 'metadata_type_ids',
  EQUIPMENT_IDS: 'equipment_ids',
  LEVEL_IDS: 'level_ids',
  ZONE_NAME_IDS: 'zone_name_ids',
  RULE_IDS: 'rule_ids',
  ARCHIVE_STATE: 'archived',
  VIEW_SAVINGS: 'view_savings',
  HAS_SAVINGS: 'has_savings',
  SELECT_ASSOCIATED_SITES: AppConstants.TICKET_SEARCH_PARAM_NAME_RESOLVE_ASSOCIATED_SITES,
  LINKED: 'linked',
  LINKED_AT_LOCAL_END: 'linked_at_local_end'
} as const;

export const UPDATED_AT_PARAM_NAMES: RelativeDateParamNames = {
  startFieldName: TicketSearchQueryParams.UPDATED_AT_LOCAL_START,
  endFieldName: TicketSearchQueryParams.UPDATED_AT_LOCAL_END,
  relativeFieldName: TicketSearchQueryParams.UPDATED_AT_RELATIVE_DATE,
  includeTodayFieldName: TicketSearchQueryParams.UPDATED_AT_INCLUDE_TODAY
};
export const CREATED_AT_PARAM_NAMES: RelativeDateParamNames = {
  startFieldName: TicketSearchQueryParams.CREATED_AT_LOCAL_START,
  endFieldName: TicketSearchQueryParams.CREATED_AT_LOCAL_END,
  relativeFieldName: TicketSearchQueryParams.CREATED_AT_RELATIVE_DATE,
  includeTodayFieldName: TicketSearchQueryParams.CREATED_AT_INCLUDE_TODAY
};
export const RESOLVED_AT_PARAM_NAMES: RelativeDateParamNames = {
  startFieldName: TicketSearchQueryParams.RESOLVED_AT_LOCAL_START,
  endFieldName: TicketSearchQueryParams.RESOLVED_AT_LOCAL_END,
  relativeFieldName: TicketSearchQueryParams.RESOLVED_AT_RELATIVE_DATE,
  includeTodayFieldName: TicketSearchQueryParams.RESOLVED_AT_INCLUDE_TODAY
};

export enum SavingsState {
  HAS_SAVINGS = 'true',
  NO_SAVINGS = 'false'
}

export enum ViewSavingsState {
  // in the future there'll be more types of savings added
  VIEW_SAVINGS_COST = 'cost'
}
/** Keys for fields in TicketSearchFormComponent */
export enum TicketSearchFields {
  SUMMARY = 'summary',
  ALERT_STATUSES = 'alert_status',
  ASSIGNEES = 'assignee',
  SITES = 'site',
  PRIORITIES = 'priorities',
  UPDATED_AT = 'updated_at',
  CREATED_AT = 'created_at',
  RESOLVED_AT = 'resolved_at',
  NOT_RESOLVED = 'not_resolved',
  IMPACTS = 'impacts',
  METADATA_TYPES = 'metadata_types',
  EQUIPMENT = 'equipment',
  LEVELS = 'levels',
  ZONE_NAMES = 'zone_names',
  VIEW_SAVINGS = 'view_savings',
  SAVINGS_STATE = 'savings_state',
  ARCHIVED = 'archived',
  RULE_TEMPLATES = 'rule_templates',
  TEMPLATE_CATEGORIES = 'template_categories',
  RULE_STATES = 'rule_states',
  TIME_IN_FAULT = 'time_in_fault',
  RULE_IDS = 'rule_ids',
  COMMENT_AUTHOR_IDS = 'comment_author_ids',
  BENEFITS = 'benefits',
  TICKET_STATUSES = 'ticket_statuses',
  TICKET_AGE = 'ticket_age',
  IN_FAULT_AT = 'in_fault_at',
  LINKED = 'linked'
}

export interface FieldConfig<U = never> {
  labelMessage?: I18nMessage;
  options?: U[];
  loading?: boolean;
}

interface TicketSearchFieldConfigWithOptions {
  [TicketSearchFields.ASSIGNEES]?: FieldConfig<User>;
  [TicketSearchFields.SITES]?: FieldConfig<SiteInfo>;
  [TicketSearchFields.PRIORITIES]?: FieldConfig<Priority>;
  [TicketSearchFields.IMPACTS]?: FieldConfig<Impact>;
  [TicketSearchFields.METADATA_TYPES]?: FieldConfig<MetadataType>;
  [TicketSearchFields.EQUIPMENT]?: FieldConfig<Equipment>;
  [TicketSearchFields.LEVELS]?: FieldConfig<Level>;
  [TicketSearchFields.ZONE_NAMES]?: FieldConfig<Zone>;
  [TicketSearchFields.RULE_TEMPLATES]?: FieldConfig<Template>;
  [TicketSearchFields.TEMPLATE_CATEGORIES]?: FieldConfig<TemplateCategoryOption>;
}

type TicketSearchFieldConfigWithoutOptions = {
  [K in Exclude<TicketSearchFields, keyof TicketSearchFieldConfigWithOptions>]?: FieldConfig;
};

export type TicketSearchFieldConfig = TicketSearchFieldConfigWithOptions & TicketSearchFieldConfigWithoutOptions;

export interface FieldLayoutConfig {
  field: TicketSearchFields;
  desktopContainerClass?: NgClass['ngClass'];
  isMobileExtraFilter: boolean;
  isDesktopExtraFilter: boolean;
}
